import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import styles from '../assets/expendituresCategories.module.css';
import { expendituresCategoriesService } from '../services/expendituresCategoriesService';

const ExpendituresCategories = () => {
  const navigate = useNavigate();
  
  // State for categories and types
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);
  

  const [loading, setLoading] = useState(true);
  const [useMockData, setUseMockData] = useState(false);

  // State for forms
  const [categoryForm, setCategoryForm] = useState({ name: '' });
  const [typeForm, setTypeForm] = useState({ name: '', category_id: '' });

  // State for modals
  const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);
  const [showEditTypeModal, setShowEditTypeModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingType, setEditingType] = useState(null);

  // State for expandable categories
  const [expandedCategories, setExpandedCategories] = useState(new Set([1, 2])); // Default expanded

  // Generate mock data
  const generateMockCategories = () => {
    return [
      {
        id: 1,
        name: 'Assets',
        special_profit_and_loss: 0,
        items: { count: () => 1664 },
        types: [
          {
            id: 31,
            name: 'Stock',
            expenditures: { count: () => 338 }
          },
          {
            id: 83,
            name: 'IT Equipment',
            expenditures: { count: () => 118 }
          },
          {
            id: 93,
            name: 'Materials',
            expenditures: { count: () => 400 }
          },
          {
            id: 123,
            name: 'Fit Out - Capital Expense',
            expenditures: { count: () => 713 }
          },
          {
            id: 129,
            name: 'My Globe',
            expenditures: { count: () => 80 }
          },
          {
            id: 130,
            name: 'Vehicle Purchase',
            expenditures: { count: () => 13 }
          },
          {
            id: 132,
            name: 'Tools',
            expenditures: { count: () => 2 }
          }
        ]
      },
      {
        id: 2,
        name: 'One Off Costs',
        special_profit_and_loss: 1,
        items: { count: () => 13222 },
        types: [
          {
            id: 1,
            name: 'Estates Gazette advertising',
            expenditures: { count: () => 41 }
          },
          {
            id: 2,
            name: 'Events',
            expenditures: { count: () => 124 }
          },
          {
            id: 3,
            name: 'Facebook',
            expenditures: { count: () => 33 }
          },
          {
            id: 4,
            name: 'Newspapers',
            expenditures: { count: () => 56 }
          },
          {
            id: 5,
            name: 'Photography',
            expenditures: { count: () => 59 }
          },
          {
            id: 6,
            name: 'Posters/Signage',
            expenditures: { count: () => 69 }
          },
          {
            id: 7,
            name: 'PR',
            expenditures: { count: () => 56 }
          },
          {
            id: 14,
            name: 'Cleaning',
            expenditures: { count: () => 80 }
          },
          {
            id: 20,
            name: 'Flooring',
            expenditures: { count: () => 26 }
          },
          {
            id: 21,
            name: 'Gardening',
            expenditures: { count: () => 156 }
          },
          {
            id: 23,
            name: 'Glazing',
            expenditures: { count: () => 5 }
          },
          {
            id: 24,
            name: 'Key Cutting',
            expenditures: { count: () => 467 }
          },
          {
            id: 26,
            name: 'Pest Control',
            expenditures: { count: () => 217 }
          }
        ]
      },
      {
        id: 3,
        name: 'Maintenance',
        special_profit_and_loss: 0,
        items: { count: () => 845 },
        types: [
          {
            id: 8,
            name: 'Regular Maintenance',
            expenditures: { count: () => 245 }
          },
          {
            id: 9,
            name: 'Emergency Repairs',
            expenditures: { count: () => 156 }
          },
          {
            id: 10,
            name: 'Preventive Maintenance',
            expenditures: { count: () => 89 }
          },
          {
            id: 11,
            name: 'Equipment Maintenance',
            expenditures: { count: () => 123 }
          },
          {
            id: 12,
            name: 'Building Maintenance',
            expenditures: { count: () => 232 }
          }
        ]
      },
      {
        id: 4,
        name: 'Utilities',
        special_profit_and_loss: 0,
        items: { count: () => 1234 },
        types: [
          {
            id: 13,
            name: 'Electricity',
            expenditures: { count: () => 456 }
          },
          {
            id: 14,
            name: 'Water',
            expenditures: { count: () => 234 }
          },
          {
            id: 15,
            name: 'Gas',
            expenditures: { count: () => 189 }
          },
          {
            id: 16,
            name: 'Internet',
            expenditures: { count: () => 156 }
          },
          {
            id: 17,
            name: 'Phone',
            expenditures: { count: () => 199 }
          }
        ]
      },
      {
        id: 5,
        name: 'Insurance',
        special_profit_and_loss: 1,
        items: { count: () => 567 },
        types: [
          {
            id: 18,
            name: 'Property Insurance',
            expenditures: { count: () => 234 }
          },
          {
            id: 19,
            name: 'Liability Insurance',
            expenditures: { count: () => 123 }
          },
          {
            id: 20,
            name: 'Equipment Insurance',
            expenditures: { count: () => 89 }
          },
          {
            id: 21,
            name: 'Vehicle Insurance',
            expenditures: { count: () => 121 }
          }
        ]
      }
    ];
  };

  // Load data
  const loadData = async () => {
    try {
      setLoading(true);
      if (useMockData) {
        const mockCategories = generateMockCategories();
        setCategories(mockCategories);
        setTypes(mockCategories.flatMap(cat => cat.types));
      } else {
        console.log('📡 [FRONTEND] Loading categories and types from API');
        
        // Call both categories and types endpoints
        const [categoriesResponse, typesResponse] = await Promise.all([
          expendituresCategoriesService.getCategories(),
          expendituresCategoriesService.getTypes()
        ]);
        
        console.log('📦 [FRONTEND] Categories Response:', categoriesResponse);
        console.log('📦 [FRONTEND] Types Response:', typesResponse);
        
        // Handle categories response
        let categories = [];
        if (categoriesResponse.data && categoriesResponse.data.length > 0) {
          // Format: { data: [{ id, name }] } - simple categories list
          console.log('✅ [FRONTEND] Setting categories:', categoriesResponse.data);
          categories = categoriesResponse.data.map(cat => ({
            id: cat.id,
            name: cat.name,
            special_profit_and_loss: 0,
            items: { count: () => 0 },
            types: []
          }));
        }
        
        // Handle types response and associate with categories
        let types = [];
        if (typesResponse.data && typesResponse.data.length > 0) {
          console.log('✅ [FRONTEND] Setting types:', typesResponse.data);
          types = typesResponse.data.map(type => ({
            ...type,
            expenditures: { count: () => 0 }
          }));
          
          // Associate types with their categories
          categories = categories.map(category => ({
            ...category,
            types: types.filter(type => type.category_id === category.id)
          }));
        }
        
                 console.log('🔗 [FRONTEND] Final categories with types:', categories);
         
         // Auto-expand categories that have types
         const categoriesWithTypes = categories.filter(cat => cat.types && cat.types.length > 0);
         if (categoriesWithTypes.length > 0) {
           const categoryIds = categoriesWithTypes.map(cat => cat.id);
           console.log('🔍 [FRONTEND] Auto-expanding categories with types:', categoryIds);
           setExpandedCategories(new Set(categoryIds));
         }
         
         setCategories(categories);
         setTypes(types);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      // Fallback to mock data on API failure
      const mockCategories = generateMockCategories();
      setCategories(mockCategories);
      setTypes(mockCategories.flatMap(cat => cat.types));
      toast.error('Failed to load categories - using offline data');
    } finally {
      setLoading(false);
    }
  };

  // Load data on mount and when useMockData changes
  useEffect(() => {
    loadData();
  }, [useMockData]);

  // Handlers for category form
  const handleCategoryFormChange = (field, value) => {
    setCategoryForm(prev => ({ ...prev, [field]: value }));
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    try {
      if (!categoryForm.name.trim()) {
        toast.error('Category name is required');
        return;
      }

      if (useMockData) {
        const newCategory = {
          id: Date.now(),
          name: categoryForm.name,
          special_profit_and_loss: 0,
          items: { count: () => 0 },
          types: []
        };
        setCategories(prev => [...prev, newCategory]);
        setCategoryForm({ name: '' });
        toast.success('Category added successfully');
      } else {
        console.log('📝 [FRONTEND] Adding category:', categoryForm);
        await expendituresCategoriesService.createCategory(categoryForm);
        setCategoryForm({ name: '' });
        console.log('🔄 [FRONTEND] Reloading data after adding category');
        await loadData();
        toast.success('Category added successfully');
      }
    } catch (error) {
      console.error('Error adding category:', error);
      toast.error('Failed to add category');
    }
  };

  // Handlers for type form
  const handleTypeFormChange = (field, value) => {
    setTypeForm(prev => ({ ...prev, [field]: value }));
  };

  const handleAddType = async (e) => {
    e.preventDefault();
    try {
      if (!typeForm.name.trim() || !typeForm.category_id) {
        toast.error('Type name and category are required');
        return;
      }

      if (useMockData) {
        const newType = {
          id: Date.now(),
          name: typeForm.name,
          category_id: parseInt(typeForm.category_id),
          expenditures: { count: () => 0 }
        };
        setTypes(prev => [...prev, newType]);
        setCategories(prev => prev.map(cat => 
          cat.id === parseInt(typeForm.category_id)
            ? { ...cat, types: [...cat.types, newType] }
            : cat
        ));
        setTypeForm({ name: '', category_id: '' });
        toast.success('Type added successfully');
      } else {
        await expendituresCategoriesService.createType(typeForm);
        setTypeForm({ name: '', category_id: '' });
        loadData();
        toast.success('Type added successfully');
      }
    } catch (error) {
      console.error('Error adding type:', error);
      toast.error('Failed to add type');
    }
  };

  // Handlers for edit modals
  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setShowEditCategoryModal(true);
  };

  const handleEditType = (type, category) => {
    setEditingType({ ...type, category_id: category.id });
    setShowEditTypeModal(true);
  };

  const handleUpdateCategory = async (e) => {
    e.preventDefault();
    try {
      if (!editingCategory.name.trim()) {
        toast.error('Category name is required');
        return;
      }

      if (useMockData) {
        setCategories(prev => prev.map(cat => 
          cat.id === editingCategory.id
            ? { ...cat, name: editingCategory.name, special_profit_and_loss: editingCategory.special_profit_and_loss }
            : cat
        ));
        setShowEditCategoryModal(false);
        setEditingCategory(null);
        toast.success('Category updated successfully');
      } else {
        await expendituresCategoriesService.updateCategory(editingCategory.id, editingCategory);
        setShowEditCategoryModal(false);
        setEditingCategory(null);
        loadData();
        toast.success('Category updated successfully');
      }
    } catch (error) {
      console.error('Error updating category:', error);
      toast.error('Failed to update category');
    }
  };

  const handleUpdateType = async (e) => {
    e.preventDefault();
    try {
      if (!editingType.name.trim()) {
        toast.error('Type name is required');
        return;
      }

      if (useMockData) {
        setCategories(prev => prev.map(cat => 
          cat.id === editingType.category_id
            ? {
                ...cat,
                types: cat.types.map(type => 
                  type.id === editingType.id
                    ? { ...type, name: editingType.name }
                    : type
                )
              }
            : cat
        ));
        setShowEditTypeModal(false);
        setEditingType(null);
        toast.success('Type updated successfully');
      } else {
        await expendituresCategoriesService.updateType(editingType.id, editingType);
        setShowEditTypeModal(false);
        setEditingType(null);
        loadData();
        toast.success('Type updated successfully');
      }
    } catch (error) {
      console.error('Error updating type:', error);
      toast.error('Failed to update type');
    }
  };

  // Handlers for delete
  const handleDeleteCategory = async (categoryId) => {
    if (window.confirm('Are you sure you want to delete this category? This will also delete all its types and related expenditures.')) {
      try {
        if (useMockData) {
          setCategories(prev => prev.filter(cat => cat.id !== categoryId));
          toast.success('Category deleted successfully');
        } else {
          const response = await expendituresCategoriesService.deleteCategory(categoryId);
          loadData();
          
          // Show detailed success message with deletion info
          if (response.data) {
            const { deleted_types, deleted_expenditures } = response.data;
            let message = 'Category deleted successfully';
            if (deleted_types > 0 || deleted_expenditures > 0) {
              message += ` (${deleted_types} types, ${deleted_expenditures} expenditures also deleted)`;
            }
            toast.success(message);
          } else {
            toast.success('Category deleted successfully');
          }
        }
      } catch (error) {
        console.error('Error deleting category:', error);
        toast.error('Failed to delete category');
      }
    }
  };

  const handleDeleteType = async (categoryId, typeId) => {
    if (window.confirm('Are you sure you want to delete this type? This will also delete all related expenditures.')) {
      try {
        if (useMockData) {
          setCategories(prev => prev.map(cat => 
            cat.id === categoryId
              ? { ...cat, types: cat.types.filter(type => type.id !== typeId) }
              : cat
          ));
          toast.success('Type deleted successfully');
        } else {
          const response = await expendituresCategoriesService.deleteType(categoryId, typeId);
          loadData();
          
          // Show detailed success message with deletion info
          if (response.data) {
            const { deleted_expenditures } = response.data;
            let message = 'Type deleted successfully';
            if (deleted_expenditures > 0) {
              message += ` (${deleted_expenditures} expenditures also deleted)`;
            }
            toast.success(message);
          } else {
            toast.success('Type deleted successfully');
          }
        }
      } catch (error) {
        console.error('Error deleting type:', error);
        toast.error('Failed to delete type');
      }
    }
  };

  // Toggle category expansion
  const toggleCategoryExpansion = (categoryId) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  if (loading) {
    return <div className={styles.loading}>Loading categories...</div>;
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumbs */}
      <div className={styles.breadcrumbs}>
        <span onClick={() => navigate('/dashboard')} className={styles.breadcrumbLink}>Dashboard</span>
        <span className={styles.breadcrumbSeparator}>/</span>
        <span onClick={() => navigate('/properties')} className={styles.breadcrumbLink}>Properties</span>
        <span className={styles.breadcrumbSeparator}>/</span>
        <span onClick={() => navigate('/expenditures')} className={styles.breadcrumbLink}>Expenditures</span>
        <span className={styles.breadcrumbSeparator}>/</span>
        <span className={styles.breadcrumbCurrent}>Expenditures categories</span>
      </div>

      {/* Debug Toggle */}
      <div className={styles.debugToggle}>
        <label>
          <input
            type="checkbox"
            checked={useMockData}
            onChange={(e) => setUseMockData(e.target.checked)}
          />
          Use Mock Data
        </label>
      </div>

      {/* Main Panel */}
      <div className={styles.panel}>
        <div className={styles.panelTitle}>
          <h3>Expenditures categories</h3>
        </div>
        <div className={styles.panelBody}>
          <div className={styles.row}>
            {/* Categories Tree */}
            <div className={styles.categoriesTree}>
              <h4>Categories tree</h4>
              <div className={styles.treeContainer}>
                <ul className={styles.tree}>
                  {categories.map(category => (
                    <li key={category.id} className={styles.categoryItem}>
                      <span 
                        className={styles.categoryName}
                        onClick={() => toggleCategoryExpansion(category.id)}
                      >
                        <i className={`fa ${expandedCategories.has(category.id) ? 'fa-folder-open' : 'fa-folder'}`}></i>
                        <i className={`fa fa-chevron-${expandedCategories.has(category.id) ? 'down' : 'right'} expandIcon`}></i>
                        {category.name} (Items: {category.types ? category.types.length : 0})
                        {category.types && category.types.length > 0 && !expandedCategories.has(category.id) && (
                          <span style={{ color: '#007bff', fontSize: '12px', marginLeft: '5px' }}>
                            [+{category.types.length} types]
                          </span>
                        )}
                        <button
                          className={styles.editButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditCategory(category);
                          }}
                          title="Edit category"
                        >
                          <i className="fa fa-edit"></i>
                        </button>
                        <button
                          className={styles.deleteButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCategory(category.id);
                          }}
                          title="Delete category"
                        >
                          <i className="fa fa-remove"></i>
                        </button>
                      </span>
                      {category.types.length > 0 && expandedCategories.has(category.id) && (
                        <ul className={styles.typesList}>
                          {category.types.map(type => (
                            <li key={type.id} className={styles.typeItem}>
                              <span className={styles.typeName}>
                                <i className="fa fa-folder"></i>
                                [{type.id}] {type.name} (Items: {type.expenditures.count()})
                                <button
                                  className={styles.editButton}
                                  onClick={() => handleEditType(type, category)}
                                  title="Edit type"
                                >
                                  <i className="fa fa-edit"></i>
                                </button>
                                <button
                                  className={styles.deleteButton}
                                  onClick={() => handleDeleteType(category.id, type.id)}
                                  title="Delete type"
                                >
                                  <i className="fa fa-remove"></i>
                                </button>
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Add Forms */}
            <div className={styles.addForms}>
              <h4>Add categories</h4>
              
              {/* Add Category Form */}
              <form onSubmit={handleAddCategory} className={styles.form}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    Name <span className={styles.required}>*</span>
                  </label>
                  <div className={styles.formInputGroup}>
                    <input
                      type="text"
                      className={styles.formInput}
                      value={categoryForm.name}
                      onChange={(e) => handleCategoryFormChange('name', e.target.value)}
                      required
                    />
                    <button type="submit" className={styles.addButton}>
                      <i className="glyphicon glyphicon-plus"></i> Add category
                    </button>
                  </div>
                </div>
              </form>

              <div className={styles.formSeparator}></div>

              {/* Add Type Form */}
              <form onSubmit={handleAddType} className={styles.form}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    Category <span className={styles.required}>*</span>
                  </label>
                  <select
                    className={styles.formSelect}
                    value={typeForm.category_id}
                    onChange={(e) => handleTypeFormChange('category_id', e.target.value)}
                    required
                  >
                                         <option value="">--- Please select category ---</option>
                     {categories.map(category => (
                       <option key={category.id} value={category.id}>
                         {category.name}
                       </option>
                     ))}
                     {/* Debug info */}
                     {categories.length === 0 && (
                       <option disabled>No categories available</option>
                     )}
                  </select>
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    Type <span className={styles.required}>*</span>
                  </label>
                  <div className={styles.formInputGroup}>
                    <input
                      type="text"
                      className={styles.formInput}
                      value={typeForm.name}
                      onChange={(e) => handleTypeFormChange('name', e.target.value)}
                      required
                    />
                    <button type="submit" className={styles.addButton}>
                      <i className="glyphicon glyphicon-plus"></i> Add type
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Category Modal */}
      {showEditCategoryModal && editingCategory && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h4>Edit category</h4>
              <button
                className={styles.modalClose}
                onClick={() => {
                  setShowEditCategoryModal(false);
                  setEditingCategory(null);
                }}
              >
                &times;
              </button>
            </div>
            <div className={styles.modalBody}>
              <form onSubmit={handleUpdateCategory}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    Name <span className={styles.required}>*</span>
                  </label>
                  <input
                    type="text"
                    className={styles.formInput}
                    value={editingCategory.name}
                    onChange={(e) => setEditingCategory(prev => ({ ...prev, name: e.target.value }))}
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    Separate for Profit and Loss
                  </label>
                  <div className={styles.radioGroup}>
                    <label>
                      <input
                        type="radio"
                        name="special_profit_and_loss"
                        value="0"
                        checked={editingCategory.special_profit_and_loss === 0}
                        onChange={(e) => setEditingCategory(prev => ({ ...prev, special_profit_and_loss: parseInt(e.target.value) }))}
                      />
                      No
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="special_profit_and_loss"
                        value="1"
                        checked={editingCategory.special_profit_and_loss === 1}
                        onChange={(e) => setEditingCategory(prev => ({ ...prev, special_profit_and_loss: parseInt(e.target.value) }))}
                      />
                      Yes
                    </label>
                  </div>
                </div>
                <div className={styles.modalActions}>
                  <button type="submit" className={styles.saveButton}>
                    <i className="glyphicon glyphicon-edit"></i> Save
                  </button>
                  <button
                    type="button"
                    className={styles.cancelButton}
                    onClick={() => {
                      setShowEditCategoryModal(false);
                      setEditingCategory(null);
                    }}
                  >
                    Close
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Type Modal */}
      {showEditTypeModal && editingType && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h4>Edit type</h4>
              <button
                className={styles.modalClose}
                onClick={() => {
                  setShowEditTypeModal(false);
                  setEditingType(null);
                }}
              >
                &times;
              </button>
            </div>
            <div className={styles.modalBody}>
              <form onSubmit={handleUpdateType}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    Name <span className={styles.required}>*</span>
                  </label>
                  <div className={styles.formInputGroup}>
                    <input
                      type="text"
                      className={styles.formInput}
                      value={editingType.name}
                      onChange={(e) => setEditingType(prev => ({ ...prev, name: e.target.value }))}
                      required
                    />
                    <button type="submit" className={styles.saveButton}>
                      <i className="glyphicon glyphicon-edit"></i> Save
                    </button>
                  </div>
                </div>
                <div className={styles.modalActions}>
                  <button
                    type="button"
                    className={styles.cancelButton}
                    onClick={() => {
                      setShowEditTypeModal(false);
                      setEditingType(null);
                    }}
                  >
                    Close
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpendituresCategories; 